import { useState, useEffect, useRef } from "react";
import { useTranslator } from "@eo-locale/react";

import {
  FiSearch,
  InputGroup,
  InputLeftElement,
  Input,
  IconButton,
  calc
} from "@/storybook-components";
import PopoverAction from "../common/popover";
import useDebounce from "@/hooks/useDebounce";
import { highlightText } from "@/helpers/highlight-text";
import { scrollIntoView } from "@/helpers/scroll-into-view";
import useClickOutsideHandler from "@/hooks/useClickOutsideHandler";

const SearchContent = ({
  isOpen,
  highlightClass = "",
  locale,
  action,
  resetPage,
  ...searchParams
}): JSX.Element => {
  const { translate } = useTranslator();
  const [search, setSearch] = useState("");
  const [isMarkVisible, setIsMarkVisible] = useState(false);

  useEffect(() => {
    if (search !== "" && search !== undefined && isMarkVisible) {
      highlightText(highlightClass, search);
    } else {
      highlightText(highlightClass, "");
    }
  }, [search, isMarkVisible, highlightClass]);

  const searchRef = useRef(null);
  const triggerDebounceRef = useRef(false);

  const { handleActive } = useClickOutsideHandler(searchRef);

  const handleChange = (event) => {
    setIsMarkVisible(false);
    setSearch(event.target.value);
  };

  const fetchSearchedList = async (text) => {
    action(searchParams.sortBy, searchParams.sortOrder, text);
    resetPage(1);
    setIsMarkVisible(true);
  };

  const debouncedValue = useDebounce(search, 1200);
  useEffect(() => {
    scrollIntoView("ideas-grid");
    if (triggerDebounceRef.current) fetchSearchedList(debouncedValue);
    triggerDebounceRef.current = true;
  }, [debouncedValue]);

  return (
    <PopoverAction
      content={
        <InputGroup w="full">
          <InputLeftElement
            pointerEvents="none"
            children={<FiSearch color="gray.800" />}
            h={{ base: 12, md: 10 }}
          />
          <Input
            placeholder={translate(`${locale}.search`)}
            borderColor="gray.500"
            focusBorderColor="none"
            color="gray.800"
            _focusVisible={{ borderColor: "gray.500" }}
            h={{ base: 12, md: 10 }}
            bg="whiteAlpha"
            onChange={handleChange}
            aria-label={translate(`${locale}.search`)}
          />
        </InputGroup>
      }
      triggerIcon={
        <IconButton
          aria-label={translate(`${locale}.search`)}
          icon={<FiSearch />}
          variant="solid"
          w={{ base: 10, sm: 12, xl: 10 }}
          h={{ base: 10, sm: 12, xl: 10 }}
          padding={2.5}
          justifyContent="center"
          alignItems="center"
          borderRadius="50%"
          bg={handleActive ? "gray.300" : "white"}
          color="gray.900"
          boxShadow="lg"
          _hover={
            handleActive
              ? { bg: "gray.300", color: "gray.900" }
              : { bg: "white", color: "gray.900" }
          }
          _focus={{
            bg: "white",
            color: "gray.900"
          }}
          ref={searchRef}
        />
      }
      bg="transparent"
      borderColor="transparent"
      maxW={{ base: "100%", md: 72 }}
      w={calc.subtract("100vw", "16px")}
      h={{ base: 12, md: 10 }}
      left={2}
    />
  );
};

export default SearchContent;
